import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router';
import { stringify } from 'query-string';

import { formatPrice } from '@dashboard/services/formatting/price';
import { AuthContext } from '@dashboard/context/AuthContext';
import { useCouponQueries } from '@dashboard/hooks/queries/useCouponQueries';
import { couponsKey } from '@dashboard/lib/queryKeys';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import Spinner from '@dashboard/components/Spinner';
import Button from '@dashboard/components/Button';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import EmptyState from '@dashboard/components/EmptyState';
import CardHeaderActions from '@dashboard/components/CardHeaderActions';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import SortInput from '@dashboard/components/Form/components/SortInput';
import TableRowElement from '@dashboard/components/TableRowElement';
import CouponStatusBadge from '@/dashboard/components/DiscountsCoupons/CouponStatusBadge';
import FilterInput from '@/dashboard/components/Form/components/FilterInput';
import { formatDate, formatDateTime } from '@/dashboard/lib/format-date';
import Dash from '@/dashboard/components/Dash';

const SORT_DIRECTIONS = {
  ASCENDING: '1',
  DESCENDING: '-1',
};

const SORT_OPTIONS = {
  NEWEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Newest first',
  },
  OLDEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Oldest first',
  },
  MOST_REDEMPTIONS: {
    value: 'timesRedeemed',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Most redemptions',
  },
  LEAST_REDEMPTIONS: {
    value: 'timesRedeemed',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Least redemptions',
  },
  LABEL_ASCENDING: {
    value: 'label',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Label A-Z',
  },
  LABELDESCENDING: {
    value: 'label',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Label Z-A',
  },
};

const FILTER_OPTIONS = {
  ALL: {
    value: 'all',
    label: 'All',
  },
  ACTIVE: {
    value: 'active',
    label: 'Active',
  },
  EXPIRED: {
    value: 'expired',
    label: 'Expired',
  },
};

function getSortOptionFromQuery(sortBy, sortDirection) {
  const sortOption = Object.keys(SORT_OPTIONS).find(
    key => SORT_OPTIONS[key].value === sortBy && SORT_OPTIONS[key].direction === sortDirection,
  );

  return sortOption || Object.keys(SORT_OPTIONS)[0];
}

function getStatusFromQuery(status) {
  const statusOption = Object.keys(FILTER_OPTIONS).find(
    key => FILTER_OPTIONS[key].value === status,
  );

  return statusOption || Object.keys(FILTER_OPTIONS)[0];
}

const DiscountsCoupons = () => {
  const query = useUrlQuery();
  const history = useHistory();
  const [sortOption, setSortOption] = useState(
    getSortOptionFromQuery(query.sortBy, query.sortDirection),
  );
  const [search, setSearch] = useState(query.search || '');
  const [status, setStatus] = useState(getStatusFromQuery(query.status));

  useEffect(() => {
    setSearch(query.search || '');
  }, [query.search]);

  useEffect(() => {
    setSortOption(getSortOptionFromQuery(query.sortBy, query.sortDirection));
  }, [query.sortBy, query.sortDirection]);

  useEffect(() => {
    setStatus(getStatusFromQuery(query.status));
  }, [query.status]);

  const sortBy = SORT_OPTIONS[sortOption].value;
  const sortDirection = SORT_OPTIONS[sortOption].direction;

  const sort = {
    [sortBy]: sortDirection,
    _id: sortDirection,
  };

  const filter = {};

  if (status === 'ACTIVE') {
    filter.$and = JSON.stringify([
      {
        $or: [
          { redeemBy: null },
          { redeemBy: { $exists: false } },
          { redeemBy: { $gte: new Date() } },
        ],
      },
      {
        $or: [
          { maxRedemptions: null },
          { maxRedemptions: { $exists: false } },
          {
            $expr: {
              $lt: ['$timesRedeemed', '$maxRedemptions'],
            },
          },
        ],
      },
    ]);
  }

  if (status === 'EXPIRED') {
    filter.$or = JSON.stringify([
      {
        $and: [
          { redeemBy: { $exists: true } },
          { redeemBy: { $ne: null } },
          { redeemBy: { $lte: new Date() } },
        ],
      },
      {
        $and: [
          { maxRedemptions: { $exists: true } },
          { maxRedemptions: { $ne: null } },
          {
            $expr: {
              $gte: ['$timesRedeemed', '$maxRedemptions'],
            },
          },
        ],
      },
    ]);
  }

  useEffect(() => {
    history.replace({
      search: stringify({
        ...query,
        sortBy: sortBy,
        sortDirection: sortDirection,
        status: FILTER_OPTIONS[status].value,
        search: search,
      }),
    });
  }, [sortBy, sortDirection, status, search]);

  return (
    <Card variant="root">
      <CardHeader variant="root" className="gap-y-2">
        <CardHeaderTitle variant="root">Coupons</CardHeaderTitle>

        <div className="w-full flex flex-col lg:flex-row gap-x-4 gap-y-2  justify-end">
          <CardHeaderActions>
            <SearchInput
              onChange={setSearch}
              name="search_customers"
              placeholder="Search by label or code"
            />
            <FilterInput options={FILTER_OPTIONS} onChange={setStatus} value={status} />
            <SortInput options={SORT_OPTIONS} onChange={setSortOption} value={sortOption} />
            <Button to="/coupons/new" appearance="primary" style={{ maxHeight: '2.5rem' }}>
              Create coupon <ArrowRightIcon className="w-4 h-4 ml-2 align-baseline" />
            </Button>
          </CardHeaderActions>
        </div>
      </CardHeader>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header">
          <CardListItemElement size="2xl">Label</CardListItemElement>
          <CardListItemElement size="fill">Code</CardListItemElement>
          <CardListItemElement align="right">Value</CardListItemElement>
          <CardListItemElement size="large">Redemptions</CardListItemElement>
          <CardListItemElement size="xl">Date</CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <CouponsList sort={sort} search={search} filter={filter} />
      </CardContent>
    </Card>
  );
};

const CouponListItem = ({ coupon }) => (
  <CardListItem href={`/coupons/${coupon._id}`} key={coupon._id}>
    <TableRowElement size="2xl">{coupon.label}</TableRowElement>
    <TableRowElement size="fill">{coupon.code}</TableRowElement>

    <TableRowElement align="right" textAlign="right">
      {coupon.amountOff
        ? formatPrice({
            amount: coupon.amountOff,
            currency: coupon.currency,
          })
        : `${coupon.percentOff}%`}
    </TableRowElement>

    <TableRowElement size="large">
      {coupon.timesRedeemed}
      {' / '}
      {coupon.maxRedemptions == null ? '∞' : coupon.maxRedemptions}
    </TableRowElement>

    <TableRowElement size="xl">{formatDateTime(coupon.createdAt)}</TableRowElement>

    <TableRowElement size="medium">
      <CouponStatusBadge coupon={coupon} />
    </TableRowElement>
  </CardListItem>
);

CouponListItem.propTypes = {
  coupon: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }),
};

const CouponsList = ({ search, sort, filter }) => {
  const { sellerId } = useContext(AuthContext);

  const { getCoupons } = useCouponQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: couponsKey({ search, sort, filter }),
    queryFn: async ({ pageParam }) =>
      await getCoupons(sellerId, {
        search,
        limit: 10,
        skip: pageParam * 10,
        ...filter,
        sort: JSON.stringify(sort),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  if (isLoading) {
    return (
      <CardContent variant="root">
        <Spinner />
      </CardContent>
    );
  }

  if (isError) {
    return (
      <CardContent variant="root">
        Unable to load coupons. Please try again or contact support.
      </CardContent>
    );
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  if (isEmpty) {
    return (
      <CardContent variant="root">
        <EmptyState>
          <EmptyState.Title>Create your first coupon code</EmptyState.Title>
          <EmptyState.Subtitle>Offer discounts to your customers.</EmptyState.Subtitle>
          <EmptyState.Action to="/coupons/new">Create coupon</EmptyState.Action>
        </EmptyState>
      </CardContent>
    );
  }

  const hasFetchedAllPages = data?.pages?.[data?.pages?.length - 1]?.has_more === false;

  return (
    <CardContent>
      <BottomScrollListener onBottom={fetchNextPage}>
        {data?.pages?.map(data =>
          data?.data.map(coupon => <CouponListItem key={coupon._id} coupon={coupon} />),
        )}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="mx-4" />}

      {hasFetchedAllPages && (
        <div className="text-sm text-center text-gray-500 mt-2">No more coupons to show</div>
      )}
    </CardContent>
  );
};

export default DiscountsCoupons;
